import { Button, Flex, Input, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { useRef } from 'react';
import { LuCopy } from 'react-icons/lu';
import { copyElementAsImage } from './screenshotUtils';

function StockAnalysisHistoryPanel({
    loading,
    error,
    history,
    growthRates,
    avg3,
    avg6,
    avg10,
    normalizedTicker,
    initialFCF,
    onInitialFCFChange,
    cashFlows,
    onApplyGrowthRate,
}) {
    const captureRef = useRef(null);

    const handleCopyAsImage = () => copyElementAsImage(captureRef.current);

    return (
        <>
            {loading && <Spinner size='xl' color='green.500' />}

            {!loading && history.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div ref={captureRef} style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                        <Flex justifyContent="space-between" alignItems="center" mb="1rem">
                            <h2 style={{ margin: 0, fontSize: '1.5em' }}>
                                Free Cash Flow History for {normalizedTicker}
                            </h2>
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <Button
                                        id="copy-table-icon-button"
                                        aria-label="Copy table as image"
                                        onClick={handleCopyAsImage}
                                        size="md"
                                        variant="ghost"
                                    >
                                        <LuCopy />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Positioner>
                                    <Tooltip.Content>Copy table as image</Tooltip.Content>
                                </Tooltip.Positioner>
                            </Tooltip.Root>
                        </Flex>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', borderBottom: '2px solid #CBD5E0' }}>Year</th>
                                    <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', borderBottom: '2px solid #CBD5E0' }}>Operating Cash Flow</th>
                                    <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', borderBottom: '2px solid #CBD5E0' }}>Capital Expenditures</th>
                                    <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', borderBottom: '2px solid #CBD5E0' }}>Free Cash Flow</th>
                                    <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', borderBottom: '2px solid #CBD5E0' }}>Growth Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((entry, idx) => {
                                    const currFCF = entry.operatingCashFlows - entry.capitalExpenditures;
                                    const gr = growthRates[idx];
                                    return (
                                        <tr key={entry.year} style={{ borderTop: '1px solid #EDF2F7' }}>
                                            <td>{entry.year}</td>
                                            <td>
                                                {entry.operatingCashFlows != null ? `$${entry.operatingCashFlows.toLocaleString()}` : '-'}
                                            </td>
                                            <td>
                                                {entry.capitalExpenditures != null ? `$${entry.capitalExpenditures.toLocaleString()}` : '-'}
                                            </td>
                                            <td>{`$${currFCF.toLocaleString()}`}</td>
                                            <td>{gr != null ? `${gr.toFixed(2)}%` : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                {[
                                    ['3-Year Average', avg3],
                                    ['6-Year Average', avg6],
                                    ['10-Year Average', avg10],
                                ].map(([label, val], i) => (
                                    <tr key={label} style={{ backgroundColor: i % 2 === 0 ? '#F7FAFC' : '#EDF2F7', borderTop: '1px solid #CBD5E0' }}>
                                        <td colSpan={4} style={{ fontWeight: 600 }}>{label}</td>
                                        <td style={{
                                            fontWeight: 600, display: 'inline-flex', alignItems: 'center',
                                            color: val == null ? undefined : val >= 0 ? '#2F855A' : '#C53030',
                                        }}>
                                            {val != null ? `${val.toFixed(2)}%` : '-'}
                                            {val != null && (
                                                <Tooltip.Root>
                                                    <Tooltip.Trigger asChild>
                                                        <Button
                                                            onClick={() => onApplyGrowthRate(parseFloat(val.toFixed(2)))}
                                                            p={0}
                                                            m={0}
                                                        >
                                                            <LuCopy />
                                                        </Button>
                                                    </Tooltip.Trigger>
                                                    <Tooltip.Positioner>
                                                        <Tooltip.Content>Use as terminal growth</Tooltip.Content>
                                                    </Tooltip.Positioner>
                                                </Tooltip.Root>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {error && <div>Error: {error}</div>}

            <Flex width='100%' justifyContent={'center'} alignItems={'center'} gap={4} mt={4} mb={4}>
                <Text fontSize="lg" fontWeight="bold">Free Cash Flow:</Text>
                <Input
                    value={initialFCF}
                    onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val) && val >= 0) {
                            onInitialFCFChange(val);
                        }
                    }}
                    width="200px"
                    textAlign="center"
                />
            </Flex>

            {cashFlows.length > 0 && (
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th>Future FCF</th>
                            <th>Discounted FCF</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cashFlows.map((flow) => (
                            <tr key={flow.year}>
                                <td>{flow.year}</td>
                                <td>${Number(flow.futureFCF).toLocaleString()}</td>
                                <td>${Math.ceil(Number(flow.discountedFCF)).toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    );
}

export default StockAnalysisHistoryPanel;
